/** Soft Deep HTTP client — same-origin `/api/*`, credentials included. */

export type ApiErrorJson = {
  code?: string;
  message?: string;
  localizeMessage?: string;
  hint?: string;
  details?: unknown;
  /** Legacy Soft body (pre ADR-0037 shape). */
  error?: string;
  [key: string]: unknown;
};

export function pickUserErrorMessage(
  j: ApiErrorJson,
  fallbackStatus?: number,
): string {
  const hint = typeof j.hint === "string" ? j.hint.trim() : "";
  let base: string | undefined;
  if (typeof j.localizeMessage === "string" && j.localizeMessage.trim()) {
    base = j.localizeMessage.trim();
  } else if (typeof j.message === "string" && j.message.trim()) {
    base = j.message.trim();
  } else if (typeof j.error === "string" && j.error.trim()) {
    base = j.error.trim();
  } else if (fallbackStatus != null) {
    base = `HTTP ${fallbackStatus}`;
  } else {
    base = "Unknown error";
  }
  if (!hint || base.includes(hint)) return base;
  return `${base} (${hint})`;
}

export function parseApiErrorBody(raw: unknown): ApiErrorJson {
  if (raw && typeof raw === "object") return raw as ApiErrorJson;
  return {};
}

export class SoftApiError extends Error {
  readonly name = "SoftApiError";
  readonly status: number;
  readonly code?: string;
  readonly canonicalMessage?: string;
  readonly body: ApiErrorJson;

  constructor(status: number, body: ApiErrorJson) {
    super(pickUserErrorMessage(body, status));
    this.status = status;
    this.body = body;
    this.code = typeof body.code === "string" ? body.code : undefined;
    this.canonicalMessage =
      typeof body.message === "string" ? body.message : undefined;
  }

  get localizeMessage(): string {
    return this.message;
  }
}

export function isSoftApiError(err: unknown): err is SoftApiError {
  return err instanceof SoftApiError;
}

export function errorMessageFromUnknown(
  err: unknown,
  fallback = "Unknown error",
): string {
  if (isSoftApiError(err)) return err.message;
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return fallback;
}

async function softApiErrorFromResponse(res: Response): Promise<SoftApiError> {
  const text = await res.text();
  let parsed: unknown = {};
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      parsed = {};
    }
  }
  return new SoftApiError(res.status, parseApiErrorBody(parsed));
}

type RequestOptions = {
  method?: string;
  json?: unknown;
  /** Fallback error code when response has no body code. */
  error?: string;
  /** Skip JSON parse (DELETE / void responses). */
  empty?: boolean;
};

export class SoftApi {
  private localeHeader(): HeadersInit {
    return { "Accept-Language": "ru" };
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = "GET", json, error = "request_failed", empty = false } = options;
    const headers = new Headers(this.localeHeader());
    if (json !== undefined) headers.set("Content-Type", "application/json");

    const res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers,
      ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    });

    if (!res.ok) {
      const err = await softApiErrorFromResponse(res);
      if (!err.code && error) {
        throw new SoftApiError(res.status, {
          ...err.body,
          code: error.toUpperCase(),
          message: err.message,
          localizeMessage: err.message,
        });
      }
      throw err;
    }

    if (empty || res.status === 204) return undefined as T;

    const text = await res.text();
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  get<T>(path: string, error = "load_failed"): Promise<T> {
    return this.request<T>(path, { error });
  }

  post<T>(path: string, json?: unknown, error = "create_failed"): Promise<T> {
    return this.request<T>(path, { method: "POST", json, error });
  }

  patch<T>(path: string, json?: unknown, error = "save_failed"): Promise<T> {
    return this.request<T>(path, { method: "PATCH", json, error });
  }

  delete(path: string, error = "delete_failed"): Promise<void> {
    return this.request<void>(path, { method: "DELETE", error, empty: true });
  }

  /** Auth helpers that inspect `ok` instead of throwing. */
  raw(
    path: string,
    init: { method?: string; json?: unknown } = {},
  ): Promise<Response> {
    const headers = new Headers(this.localeHeader());
    if (init.json !== undefined) headers.set("Content-Type", "application/json");
    return fetch(path, {
      method: init.method ?? "GET",
      credentials: "same-origin",
      headers,
      ...(init.json !== undefined ? { body: JSON.stringify(init.json) } : {}),
    });
  }
}

/** App-wide Soft API singleton. */
export const softApi = new SoftApi();
